import { RoleBasedRoutes, Routes } from 'app/config/routes/consts';
import { validateToken } from 'entities/Auth/api';
import { useAuthStore } from 'processes/auth/model/state';
import { FunctionComponent, ReactElement, useMemo } from 'react';
import { useQuery } from 'react-query';
import { Navigate } from 'react-router-dom';
import { endImpersonation, isImpersonationMode } from 'shared/helpers/impersonation';
import { useBasePath } from 'shared/helpers/useBasePath';
import { Loader } from 'shared/ui/Loader';

interface Props {
	children: ReactElement;
}

export const AuthorisedLayout: FunctionComponent<Props> = ({ children }) => {
	const basePath = useBasePath();
	const { accessToken, user, company, permissions, isAdmin, isUser, updateAuthData } = useAuthStore();

	// In impersonation mode the tab starts with the token only, so the
	// user/company have to be fetched before the layout can render
	const needsImpersonationBootstrap = isImpersonationMode() && !!accessToken && (!user || !company);

	useQuery({
		queryKey: ['impersonation-bootstrap'],
		queryFn: () => validateToken(),
		enabled: needsImpersonationBootstrap,
		onSuccess: (response) => {
			updateAuthData(response.data);
		},
		onError: () => {
			endImpersonation();
		},
	});

	const currentRoute = useMemo(() => {
		return Object.values(RoleBasedRoutes).find((route) => {
			return route.path === basePath;
		});
	}, [basePath]);

	if (needsImpersonationBootstrap) {
		return <Loader />;
	}

	if (!accessToken || !user || !company) {
		return <Navigate to={'/sign-in'} replace />;
	}

	if (!currentRoute?.access.includes(user.role) && isAdmin) {
		return <Navigate to={Routes.users} replace />;
	}

	if (!currentRoute?.access.includes(user.role)) {
		return <Navigate to={Routes.startRecognition} replace />;
	}

	const isOwner = isUser || isAdmin;
	if (currentRoute?.permission && !isOwner && !permissions?.includes(currentRoute.permission)) {
		return <Navigate to={Routes.startRecognition} replace />;
	}

	return <>{children}</>;
};
